import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
//import PercentIcon from "@mui/icons-material/Percent";
//import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../../assets/styles/tokens";
// ─────────────────────────────────────────────────────────────────────────────

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

// ─── Local state shape (mirrors Redux sortForBudget) ──────────────────────────
interface SortState {
  keytoSort: string;
  label: string;
  direction: "asc" | "desc";
}

const defaultSort: SortState = {
  keytoSort: "perc_Qftd_budget_use",
  label: "Budget Usage",
  direction: "asc",
};
// ─────────────────────────────────────────────────────────────────────────────

export default function NoPlayerSortBy() {
  const [open, setOpen] = React.useState(false);

  // Replaces Redux state
  const [appliedSort, setAppliedSort] = React.useState<SortState>(defaultSort);
  const [sortedData, setSortedData] = React.useState<SortState>(defaultSort);

  const toggleDrawer = (newOpen: boolean) => () => {
    if (newOpen) setSortedData(appliedSort); // reset pending to applied on open
    setOpen(newOpen);
  };

  const handleToggle = (value: string, label: string) => {
    setSortedData((prev) => ({
      keytoSort: value,
      label,
      direction:
        prev.keytoSort === value && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Replaces dispatch(setSortForBudget(...))
  const handleApplySorting = () => {
    setAppliedSort(sortedData);
    setOpen(false);
  };

  const options = [
    {
      label: "Brand Name",
      value: "brand_name",
      sort_icon: <ArrowUpwardIcon />,
      icon: undefined,
    },
    {
      label: "Lack of Player Report",
      value: "lack_player_report",
      sort_icon: <ArrowUpwardIcon />,
      icon: undefined,
    },
    {
      label: "Brand Action Date",
      value: "brand_action_date",
      sort_icon: <ArrowUpwardIcon />,
      icon: undefined,
    },
    {
      label: "NG FTD",
      value: "ng_ftd",
      sort_icon: <ArrowUpwardIcon />,
      icon: undefined,
    },
    {
      label: "BR FTD",
      value: "br_ftd",
      sort_icon: <ArrowUpwardIcon />,
      icon: undefined,
    },
  ];

  const ShortList = (
    <Box role="shortBy">
      <Puller />
      <Stack justifyContent="end" direction="row" p={1}>
        <IconButton
          aria-label="Close"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        Sort By
      </Typography>

      <Box sx={{ my: 2 }}>
        {options.map(({ label, value, sort_icon, icon }) => {
          const isCurrentSort = sortedData.keytoSort === value;

          return (
            <Button
              key={value}
              onClick={() => handleToggle(value, label)}
              startIcon={
                isCurrentSort ? (
                  sortedData.direction === "asc" ? (
                    React.cloneElement(sort_icon, {
                      sx: {
                        position: "absolute",
                        left: "7px",
                        top: "7px",
                        transition: "0.2s",
                        width: spacing.Spacing.lg.$value,
                      },
                    })
                  ) : (
                    <ArrowUpwardIcon
                      sx={{
                        position: "absolute",
                        left: "7px",
                        top: "7px",
                        transform: "rotate(180deg)",
                        transition: "0.2s",
                        width: spacing.Spacing.lg.$value,
                      }}
                    />
                  )
                ) : (
                  <ArrowUpwardIcon
                    sx={{
                      position: "absolute",
                      left: "7px",
                      top: "7px",
                      opacity: 0,
                      width: spacing.Spacing.lg.$value,
                    }}
                  />
                )
              }
              endIcon={icon}
              variant="outlined"
              color="secondary"
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: typography.Size.Text.sm.$value + "!important",
                },
                borderRadius: 2,
                mr: spacing.Spacing.sm.$value,
                mb: spacing.Spacing.sm.$value,
                py: spacing.Spacing.xs.$value,
                paddingRight: spacing.Spacing["1xl"].$value,
                paddingLeft: spacing.Spacing.xl.$value,
                textTransform: "capitalize",
                fontWeight: typography.Font_weight.Regular.$value,
                border: "1px solid " + colors.GrayLightMode[200].$value,
                color: colors.GrayLightMode[700].$value,
                fontSize: typography.Size.Text.xs.$value,
                ...(isCurrentSort && {
                  border:
                    "1px solid " + colors.Brand[600].$value + "!important",
                  boxShadow: "none !important",
                  color: colors.GrayLightMode[900].$value,
                }),
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          textTransform: "capitalize",
          boxShadow: "none !important",
          background: colors.Brand[600].$value,
          borderRadius: 2,
        }}
        onClick={handleApplySorting}
      >
        Apply
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        onClick={toggleDrawer(true)}
        component="span"
        display={"flex"}
        alignItems={"center"}
        gap={0.5}
        sx={{
          fontWeight: typography.Font_weight.Medium.$value,
          color: colors.Brand[700].$value,
        }}
      >
        Brand Name{" "}
        <ArrowDownwardIcon
          sx={{
            color: colors.Brand[700].$value,
            fontSize: typography.Size.Text.xl.$value,
          }}
        />
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {ShortList}
      </Drawer>
    </>
  );
}
